import { PlusCircleOutlined } from '@ant-design/icons';
import AdminActions from '@components/AdminActions';
import Table from '@components/Table';
import useSwr from '@hooks/useSwr';
import SessionContext from '@providers/Session/SessionContext';
import api from '@services/api';
import { extractDateInISOFormat } from '@utils/formatters';
import { Button, Input, message, Select, Tooltip } from 'antd';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useSearchParams from '@hooks/useSearchParams';
import { useTranslation } from 'react-i18next';
import ApnModal from './ApnModal';

const { Search } = Input;

type ApnDataType = {
  accounting: number | null;
  apnName: string;
  authentication: number | null;
  createdAt: string;
  createdBy: string;
  disconnect: number | null;
  id: number;
  updatedAt: string | null;
  updatedBy: string | null;
  operator: string | null;
};

type ApnDataResponse = {
  content: ApnDataType[];
  totalElements: number;
};

const APNs: React.FC = () => {
  const { t } = useTranslation('customers');
  const { hasPermission } = useContext(SessionContext);

  const [modalVisible, setModalVisible] = useState(false);
  const [currentApn, setCurrentApn] = useState<ApnDataType>();
  const [isEditing, setIsEditing] = useState(false);
  const [apnNameValue, setapnNameValue] = useState<string | undefined>(
    undefined,
  );

  const {
    setSearchParams,
    searchParams,
    hasValuesInExtraParams,
    clearSearchParams,
  } = useSearchParams({
    type: 'pagination',
    extraParams: [
      'apnName',
      'accounting',
      'authentication',
      'disconnect',
      'operator',
    ],
  });

  const {
    page,
    linesPerPage,
    apnName,
    accounting,
    authentication,
    disconnect,
    operator,
  } = useMemo(() => searchParams, [searchParams]);

  const {
    data: apns,
    mutate,
    isLoading,
  } = useSwr<ApnDataResponse>('/service-proxy/apns', {
    params: {
      page: page ? Number(page) - 1 : 0,
      linesPerPage: linesPerPage ?? 20,
      apnName: apnName && apnName.toLowerCase(),
      accounting,
      authentication,
      disconnect,
      operator,
    },
  });

  const { data: operatorsData } = useSwr('/service-proxy/mot-operators');

  const operatorsOptions = useMemo(
    () =>
      operatorsData?.map(({ name }: { name: string }) => ({
        label: name,
        value: name,
      })),
    [operatorsData],
  );

  const deleteApn = useCallback(
    (id: string) => api.delete(`service-proxy/apn/delete/${id}`),
    [],
  );

  const handleActions = useCallback(
    async (actionType: 'UPDATE' | 'DELETE', apnData: ApnDataType) => {
      if (actionType === 'UPDATE') {
        setCurrentApn(apnData);
        setIsEditing(true);
        setModalVisible(true);
      } else {
        try {
          await deleteApn(String(apnData.id));
          mutate();
          message.success(t('apns.msgs.success-deleting-apn'));
        } catch (error: any) {
          if (error.response.status === 422) {
            message.error(t('apns.msgs.custom-error-deleting-apn'));
            return;
          }
          message.error(t('apns.msgs.error-deleting-apn'));
        }
      }
    },
    [deleteApn, t, mutate],
  );

  const columns = useMemo(() => {
    const defaultColumns: any = [
      {
        title: 'APN',
        dataIndex: 'apnName',
        key: 'apnName',
      },
      {
        title: t('apns.accounting'),
        dataIndex: 'accounting',
        key: 'accounting',
        align: 'center' as const,
        render: (value: number) => (value === 1 ? t('apns.yes') : t('apns.no')),
      },
      {
        title: t('apns.authentication'),
        dataIndex: 'authentication',
        key: 'authentication',
        align: 'center' as const,
        render: (value: number) => (value === 1 ? t('apns.yes') : t('apns.no')),
      },
      {
        title: t('apns.disconnect'),
        dataIndex: 'disconnect',
        key: 'disconnect',
        align: 'center' as const,
        render: (value: number) => (value === 1 ? t('apns.yes') : t('apns.no')),
      },
      {
        title: t('apns.createdAt'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        align: 'center' as const,
        render: (value: string) => {
          return extractDateInISOFormat(value);
        },
      },
      {
        title: t('apns.operator'),
        dataIndex: 'operator',
        key: 'operator',
        align: 'center' as const,
      },
    ];
    if (hasPermission('manage-apn', 'portal')) {
      defaultColumns.push({
        title: t('apns.action'),
        key: 'actions',
        align: 'center' as const,
        render: (_: any, record: ApnDataType) => (
          <>
            <AdminActions
              alertDelete={t('apns.msgs.delete')}
              entityToExclude={String(record.apnName)}
              onDelete={() => handleActions('DELETE', record)}
            />
          </>
        ),
      });
    }
    return defaultColumns;
  }, [handleActions, t, hasPermission]);

  const handleModalClose = () => {
    setModalVisible(false);
    mutate();
    setIsEditing(false);
  };

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('attributes:name'),
        element: (
          <Search
            onSearch={(value: string) => {
              setSearchParams({ apnName: value });
            }}
            value={apnNameValue}
            onChange={({ target: { value } }) => setapnNameValue(value)}
            allowClear
            className="guide-search"
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('apns.operator'),
        element: (
          <Select
            options={operatorsOptions}
            style={{ width: '100%' }}
            allowClear
            value={operator}
            onChange={value => {
              setSearchParams({
                operator: value,
              });
            }}
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('apns.accounting'),
        element: (
          <Select
            options={[
              {
                label: t('apns.yes'),
                value: '1',
              },
              {
                label: t('apns.no'),
                value: '0',
              },
            ]}
            style={{ width: '100%' }}
            allowClear
            value={accounting}
            onChange={value => {
              setSearchParams({
                accounting: value,
              });
            }}
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('apns.authentication'),
        element: (
          <Select
            options={[
              {
                label: t('apns.yes'),
                value: '1',
              },
              {
                label: t('apns.no'),
                value: '0',
              },
            ]}
            style={{ width: '100%' }}
            allowClear
            value={authentication}
            onChange={value => {
              setSearchParams({
                authentication: value,
              });
            }}
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('apns.disconnect'),
        element: (
          <Select
            options={[
              {
                label: t('apns.yes'),
                value: '1',
              },
              {
                label: t('apns.no'),
                value: '0',
              },
            ]}
            style={{ width: '100%' }}
            allowClear
            value={disconnect}
            onChange={value => {
              setSearchParams({
                disconnect: value,
              });
            }}
          />
        ),
      },
    ],
    [
      t,
      apnNameValue,
      operatorsOptions,
      operator,
      accounting,
      authentication,
      disconnect,
      setSearchParams,
    ],
  );

  useEffect(() => {
    setapnNameValue(apnName);
  }, [apnName]);

  return (
    <div>
      <Table
        columns={columns}
        data={apns?.content ?? []}
        loading={isLoading && !apns}
        tableKey="apns"
        title={t('apns.titles.main')}
        extraActions={
          hasPermission('manage-apn', 'portal') && (
            <>
              <Tooltip title={t(`apns.titles.new-apn`)}>
                <Button
                  icon={<PlusCircleOutlined />}
                  onClick={() => setModalVisible(true)}
                />
              </Tooltip>
            </>
          )
        }
        filters={{
          list: tableFilters,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
        }}
        pagination={{ page, linesPerPage, total: apns?.totalElements }}
        onTableChange={setSearchParams}
      />
      <ApnModal
        visible={modalVisible}
        onClose={handleModalClose}
        apn={currentApn}
        isEditing={isEditing}
        operatorsOptions={operatorsOptions}
      />
    </div>
  );
};

export default APNs;
